import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from "react-select";

import CustomGroupsOption from 'components/Companies/Company/AssetEditor/EnterpriseAssociations/CustomGroupsOption';
import isEqual from "lodash/isEqual";

import { Tooltip } from 'components/Common/Tooltip/Tooltip';
import { darkModeSelectStyles } from 'components/Common/CustomStyles/darkModeSelectStyles';

function CustomGroupSelect({ groups, handleSelectedGroups, assignmentId }) {

  const darkMode = useSelector(state => state.app.darkMode);

  const [groupTree, setGroupTree] = useState([]);
  const [groupTreeIsEmpty, setGroupTreeIsEmpty] = useState(true);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mappedGroupTree, setMappedGroupTree] = useState([]);

  useEffect(() => {
    const groupIds = new Set(groups?.map((gt) => gt?.id) ?? []);
    const filteredOptions = selectedGroups?.filter((sg) =>
      groupIds.has(sg?.value)
    ) ?? [];
    setFilteredGroups(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [JSON.stringify(groups), selectedGroups]);
  
  useEffect(() => {
    handleSelectedGroups(assignmentId, filteredGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, filteredGroups])

  useEffect(() => {
    setGroupTree(groups.filter(g => !(g.isRoot || g.isAllSites)));
  }, [groups])
  
  useEffect(() => {
    setGroupTreeIsEmpty(Array.isArray(groupTree) && groupTree.length <= 0);
  }, [groupTree])

  useEffect(() => {
    if (groupTree) {
      
      const mapped = groupTree.map((group) => {
        const { id, name, isParent, parent_id } = group;

        let formatName = "";
        let remainingText = name;

        for (let i = 0; i < name?.length; i++) {
          if (name[i] === "_") {
            formatName = formatName + "\u00A0\u00A0\u00A0";
            remainingText = name.slice(i+1);
          } else {
            break;
          }
        }
        formatName = formatName + remainingText;

        return (
          ({ label: formatName, value: id, isParent, parent_id, isDisabled: isParent, })
        );
      })
      // .filter(g => !selectedGroups.includes(g.id));

      setMappedGroupTree(prev => {
        if (isEqual(prev, mapped)) return prev;
        return mapped;
      });

    }
  }, [groupTree])

  const handleSelectedOptions = (data, selectedOptions) => {
  
      // handler functions ------------------------------------------------>
      switch (data.action) {
        case "remove-value":
          setSelectedGroups(selectedOptions);
          break;
  
        case "pop-value":
          setSelectedGroups(selectedOptions);
          break;
  
        case "clear":
          setSelectedGroups([]);
          break;
        
        case "select-option":
          setSelectedGroups(selectedOptions)
          break;
  
        default:
          break;
      }
    }

  return (
    <>
      {groupTreeIsEmpty ?
        <>
          <Tooltip
            description='
              Either no new custom groups have been created, or all available new groups have been selected.
              So these sites will be added to the default  "All Sites" group.
            '
            placement='top'
          >
            <span className='w-100'>
              <Select
                data-intercom-target={"select-new-enterprise-group-select"}
                name="enterprise_groups_edit"
                // id="group-options-select"
                classNamePrefix="group-options"
                className="w-100"
                options={groupTree}
                value={null}
                placeholder="All Sites"
                isDisabled={true}
                styles={{...(darkMode ? darkModeSelectStyles : "")}}
              />
            </span>
          </Tooltip>
        </>
      :
        <Select
          data-intercom-target={"select-new-enterprise-group-select"}
          name="enterprise_groups_assign"
          id="group-options-select"
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          classNamePrefix="group-options"
          className={`w-100 ${menuOpen ? "menu-open" : ""}`}
          components={{ Option: CustomGroupsOption }}
          isMulti
          options={mappedGroupTree}
          value={filteredGroups ?? []}
          placeholder="All Sites"
          maxMenuHeight={200}
          onChange={(selectedOptions, actionMeta) => {
            const formattedOptions = selectedOptions?.map(option => ({
              ...option,
              label: option.label
                ? option.label.replace(/^[\u00A0]+/, "")
                : option.label,
            }));

            handleSelectedOptions(actionMeta, formattedOptions);
          }}

          styles={{
            ...(darkMode ? darkModeSelectStyles : {}),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            container: (base) => ({
              ...base,
              ...(menuOpen && {
                position: 'relative',
                zIndex: 9999,
              }),
            }),
            option: (base, state) => ({
              ...base,
              ...(darkMode && {
                color: 'white',
                borderColor: 'white',
                backgroundColor: '#13274d',
                '&:hover': {
                  backgroundColor: '#b9bfca',
                },
              }),
              ...(state.isDisabled && {
                pointerEvents: 'none',
                opacity: 0.5,
              }),
              ...(state.isDisabled && state.isFocused && {
                backgroundColor: 'white',
              }),
            }),
          }}
        />
      }
    </>
  )
}

export default CustomGroupSelect;
